// ============================================
// NRS.05 — Intake Modal Orchestration Types
// ============================================
// Schema chain: ModalMarker → ModalSubmission → DataCompletenessFlags
// Contract: Flag persistence on submit, duplicate suppression, sex-gating
// ============================================

import type { DataCompletenessFlags } from './triageSession';

// ── Modal State ──

export type ModalType = 'goals' | 'medications' | 'allergies' | 'pregnancy';

export type ModalStatus = 'pending' | 'shown' | 'submitted' | 'dismissed';

export interface ModalState {
  type: ModalType;
  status: ModalStatus;
  shownAt: Date | null;
  completedAt: Date | null;
  /** Number of times this modal has been shown (for dedup) */
  showCount: number;
}

// ── Modal Submission ──

export interface ModalSubmission {
  modalType: ModalType;
  action: 'submit' | 'dismiss';
  data: ModalSubmissionData;
  submittedAt: Date;
}

export type ModalSubmissionData =
  | GoalsSubmission
  | MedicationsSubmission
  | AllergiesSubmission
  | PregnancySubmission;

export interface GoalsSubmission {
  type: 'goals';
  goals: string[];
}

export interface MedicationsSubmission {
  type: 'medications';
  medications: string[];
}

export interface AllergiesSubmission {
  type: 'allergies';
  allergies: string[];
}

export interface PregnancySubmission {
  type: 'pregnancy';
  isPregnant: boolean;
  isTryingToConceive: boolean;
  isBreastfeeding: boolean;
}

// ── Modal Orchestrator State ──

export interface ModalOrchestratorState {
  modals: Record<ModalType, ModalState>;
  dataCompletenessFlags: DataCompletenessFlags;
  /** Patient sex — used for pregnancy modal gating. undefined = conservative (show pregnancy modal) */
  patientSex: 'female' | 'male' | undefined;
  /** Whether all required modals have been shown */
  allModalsPresented: boolean;
}

// ── Marker-to-Modal Mapping ──

export const MARKER_TO_MODAL: Record<string, ModalType> = {
  '[SHOW_GOALS_MODAL]': 'goals',
  '[SHOW_MEDS_MODAL]': 'medications',
  '[SHOW_ALLERGIES_MODAL]': 'allergies',
  '[SHOW_PREGNANCY_MODAL]': 'pregnancy',
};

// ── Modal Sequence ──

export const MODAL_SEQUENCE_FEMALE: ModalType[] = ['goals', 'medications', 'allergies', 'pregnancy'];
export const MODAL_SEQUENCE_MALE: ModalType[] = ['goals', 'medications', 'allergies'];

// ── Metrics ──

export interface IntakeModalMetrics {
  totalCalls: number;
  successRate: number;
  avgLatencyMs: number;
  errorsByType: Record<string, number>;
  accuracyOnGoldStandard: number;
}
